package com.exura.pages.planCreator.components.seriesViews.sections

import androidx.compose.runtime.*
import com.exura.components.Icon
import com.exura.features.plandefinition.domain.model.series.SimpleQuestionSeries
import com.exura.pages.planCreator.components.seriesViews.inputs.PlanCreatorInputMode
import com.exura.pages.planCreator.components.seriesViews.inputs.PlanCreatorTextInput
import com.exura.ui.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px

/**
 * Full-featured question options input with complete control over all fields:
 * - id (auto-generated UUID)
 * - text (editable)
 * - responseMessage (optional, editable)
 * - isResponsePositive (toggle)
 */
@Composable
internal fun QuestionOptionsSection(
    options: List<SimpleQuestionSeries.QuestionOption>,
    onOptionsChange: (List<SimpleQuestionSeries.QuestionOption>) -> Unit,
    mode: PlanCreatorInputMode,
    enabled: Boolean = true,
) = EditViewSection(
    enabled = enabled,
    title = "Opcje odpowiedzi",
    withTitle = false,
) {
    var lastOption by remember(options) { mutableStateOf(
        options.map { q -> q.id.toIntOrNull() }.filterNotNull().maxOrNull())
    }

    Column(
        verticalArrangement = Arrangement.spacedBy(16.px),
        modifier = Modifier.fillMaxWidth(),
    ) {
        // Existing options - organized in a flexible row layout
        if (options.isNotEmpty()) {
            // Group options in rows to utilize full width
            val optionsPerRow = 4 // Adjust based on desired layout
            val optionRows = options.chunked(optionsPerRow)

            optionRows.forEachIndexed { rowIndex, rowOptions ->
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(16.px)
                ) {

                    rowOptions.forEachIndexed { colIndex, option ->
                        val globalIndex = rowIndex * optionsPerRow + colIndex

                        QuestionOptionEditor(
                            option = option,
                            index = globalIndex,
                            onOptionChange = { newOption ->
                                val newOptions = options.toMutableList()
                                newOptions[globalIndex] = newOption
                                onOptionsChange(newOptions)
                            },
                            onRemoveOption = {
                                val newOptions = options.toMutableList()
                                newOptions.removeAt(globalIndex)
                                onOptionsChange(newOptions)
                            },
                            mode = mode,
                            canRemove = options.size > 1, // Must have at least one option
                            modifier = Modifier.weight(1f).fillMaxWidth() // Equal width distribution
                        )
                    }

                    // Fill remaining space if odd number of options in row
                    if (rowOptions.size < optionsPerRow && rowIndex > 0) {
                        repeat(optionsPerRow - rowOptions.size) {
                            Box(modifier = Modifier.weight(1f).fillMaxWidth())
                        }
                    }

                }
            }
        }

        // Add new option button (only in edit mode) - spans full width
        if (mode == PlanCreatorInputMode.EDIT && enabled) {
            AddOptionButton(
                onClick = {
                    val newId = lastOption?.inc() ?: 0
                    lastOption = newId
                    val newOption = SimpleQuestionSeries.QuestionOption(
                        id = newId.toString(),
                        text = "",
                        responseMessage = null,
                        isResponsePositive = true
                    )
                    onOptionsChange(options + newOption)
                },
                modifier = Modifier.fillMaxWidth()
            )
        }

        // Validation help text
        if (options.isEmpty()) {
            SpanText(
                text = "Dodaj przynajmniej jedną opcję odpowiedzi",
                modifier = Modifier
                    .classNames(TextStyles.normal12)
                    .color(ExuraColors.RED)
            )
        } else if (options.any { it.text.isBlank() }) {
            SpanText(
                text = "Wszystkie opcje muszą mieć tekst",
                modifier = Modifier
                    .classNames(TextStyles.normal12)
                    .color(ExuraColors.ORANGE)
            )
        }
    }
}

@Composable
private fun QuestionOptionEditor(
    option: SimpleQuestionSeries.QuestionOption,
    index: Int,
    onOptionChange: (SimpleQuestionSeries.QuestionOption) -> Unit,
    onRemoveOption: () -> Unit,
    mode: PlanCreatorInputMode,
    canRemove: Boolean = true,
    modifier: Modifier = Modifier
) {
    var optionText by remember(option.id) { mutableStateOf(option.text) }
    var responseMessage by remember(option.id) { mutableStateOf(option.responseMessage ?: "") }
    var isResponsePositive by remember(option.id) { mutableStateOf(option.isResponsePositive) }
    var showResponseMessage by remember(option.id) { mutableStateOf(option.responseMessage != null) }

    // Update parent when local state changes
    LaunchedEffect(optionText, responseMessage, isResponsePositive, showResponseMessage) {
        val finalResponseMessage = if (showResponseMessage && responseMessage.isNotBlank()) {
            responseMessage
        } else {
            null
        }

        onOptionChange(
            option.copy(
                text = optionText,
                responseMessage = finalResponseMessage,
                isResponsePositive = isResponsePositive
            )
        )
    }

    Column(
        modifier = modifier
            .borderRadius(8.px)
            .border {
                width(1.px)
                style(LineStyle.Solid)
                color(ExuraColors.LIGHT_GRAY)
            }
            .padding(16.px),
        verticalArrangement = Arrangement.spacedBy(12.px)
    ) {
        // Header with remove button
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                text = "Opcja ${index + 1}",
                modifier = Modifier
                    .classNames(TextStyles.medium14)
                    .color(ExuraColors.ARSENIC)
            )

            if (canRemove && mode == PlanCreatorInputMode.EDIT) {
                Icon(
                    iconSrc = Assets.IconsUntiled.DELETE_PLAN,
                    size = 16.px,
                    color = ExuraColors.RED,
                    modifier = Modifier
                        .cursor(Cursor.Pointer)
                        .onClick { onRemoveOption() },
                )
            }
        }

        // Option text input
        PlanCreatorTextInput(
            value = optionText,
            onValueChange = { optionText = it },
            mode = mode,
            placeholder = "Tekst opcji...",
            modifier = Modifier.fillMaxWidth()
        )

        // Response message toggle and input
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.px)
        ) {
            if (mode == PlanCreatorInputMode.EDIT) {
                // Checkbox for showing response message
                Box(
                    modifier = Modifier
                        .size(16.px)
                        .border {
                            width(1.px)
                            style(LineStyle.Solid)
                            color(ExuraColors.GRAY)
                        }
                        .backgroundColor(if (showResponseMessage) ExuraColors.BLUE else ExuraColors.WHITE)
                        .cursor(Cursor.Pointer)
                        .onClick { showResponseMessage = !showResponseMessage }
                )
            }

            SpanText(
                text = "Wiadomość zwrotna",
                modifier = Modifier
                    .classNames(TextStyles.normal12)
                    .color(ExuraColors.ARSENIC)
            )
        }

        if (showResponseMessage) {
            PlanCreatorTextInput(
                value = responseMessage,
                onValueChange = { responseMessage = it },
                mode = mode,
                placeholder = "Wiadomość dla użytkownika...",
                modifier = Modifier.fillMaxWidth()
            )

            // Positive/negative toggle
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.px)
            ) {
                SpanText(
                    text = "Pozytywna odpowiedź:",
                    modifier = Modifier
                        .classNames(TextStyles.normal12)
                        .color(ExuraColors.ARSENIC)
                )

                if (mode == PlanCreatorInputMode.EDIT) {
                    Box(
                        modifier = Modifier
                            .size(16.px)
                            .border {
                                width(1.px)
                                style(LineStyle.Solid)
                                color(ExuraColors.GRAY)
                            }
                            .backgroundColor(if (isResponsePositive) ExuraColors.GREEN else ExuraColors.RED)
                            .cursor(Cursor.Pointer)
                            .onClick { isResponsePositive = !isResponsePositive }
                    )
                }

                SpanText(
                    text = if (isResponsePositive) "Tak" else "Nie",
                    modifier = Modifier
                        .classNames(TextStyles.normal12)
                        .color(if (isResponsePositive) ExuraColors.GREEN else ExuraColors.RED)
                )
            }
        }
    }
}

@Composable
private fun AddOptionButton(
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier
            .height(48.px)
            .backgroundColor(ExuraColors.LIGHT_GRAY.copy(alpha = 0.3))
            .border {
                width(1.px)
                style(LineStyle.Dashed)
                color(ExuraColors.GRAY)
            }
            .borderRadius(8.px)
            .cursor(Cursor.Pointer)
            .onClick { onClick() },
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(
            iconSrc = Assets.IconsUntiled.ADD_QUESTION_OPTION,
            size = 16.px,
            color = ExuraColors.BLUE,
        )

        SpanText(
            text = "Dodaj opcję",
            modifier = Modifier
                .padding(left = 8.px)
                .classNames(TextStyles.medium14)
                .color(ExuraColors.BLUE)
        )
    }
}
